"use client";

import { useCallback, useRef, useState } from "react";

import Button from "@/components/library/Button";
import clsx from "clsx";
import { isPersonalEmail } from "@/utils/personalDomains";
import { useTranslation } from "react-i18next";

interface EmailInputProps {
  onSubmit: (email: string) => void;
  isLoading: boolean;
  variant?: "dark" | "light";
}

const EmailInput = ({
  onSubmit,
  isLoading,
  variant = "dark",
}: EmailInputProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [showPersonalWarning, setShowPersonalWarning] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const triggerShake = useCallback(() => {
    setHasError(true);
    inputRef.current?.focus();
    containerRef.current?.classList.remove("animate-hero-shake");
    // Force reflow to restart animation
    containerRef.current?.getBoundingClientRect();
    containerRef.current?.classList.add("animate-hero-shake");
    setTimeout(() => setHasError(false), 1500);
  }, []);

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;

    const trimmed = email.trim();
    if (!trimmed?.includes("@")) {
      triggerShake();
      return;
    }

    if (isPersonalEmail(trimmed) && !showPersonalWarning) {
      setShowPersonalWarning(true);
      return;
    }

    setShowPersonalWarning(false);
    onSubmit(trimmed);
  };

  const handleContinueAnyway = () => {
    setShowPersonalWarning(false);
    onSubmit(email.trim());
  };

  const errorClasses = hasError ? "ring-2 ring-red-300 animate-hero-shake" : "";

  return (
    <div className="w-full max-w-xl mx-auto">
      <form onSubmit={handleSubmit}>
        <div
          ref={containerRef}
          className={clsx(
            "relative flex items-center",
            "bg-white",
            "rounded-full",
            "p-1 pl-5",
            "shadow-lg",
            "transition-all duration-300",
            errorClasses,
          )}
        >
          <svg
            className="w-5 h-5 text-black/30 shrink-0 mr-2"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
          </svg>
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (showPersonalWarning) setShowPersonalWarning(false);
              if (hasError) setHasError(false);
            }}
            placeholder={t("hero.emailPlaceholder")}
            disabled={isLoading}
            className={clsx(
              "flex-1 bg-transparent",
              "text-black placeholder:text-black/30",
              "focus:outline-none",
              "text-base",
              "disabled:opacity-50",
              "min-w-0",
              "py-2",
            )}
          />
          <Button
            htmlType="submit"
            type="secondary"
            disabled={isLoading}
            className={clsx(
              "shrink-0",
              "active:scale-[0.97] transition-all duration-150 font-medium",
            )}
          >
            {isLoading ? (
              <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              t("hero.goButton")
            )}
          </Button>
        </div>
      </form>

      {showPersonalWarning && (
        <div
          className={clsx(
            "mt-3 px-4 py-3 rounded-xl text-sm text-center animate-[fadeSlideIn_0.3s_ease-out_both]",
            variant === "dark"
              ? "bg-white/10 backdrop-blur-sm text-white"
              : "bg-black/5 text-black/80",
          )}
        >
          <p>{t("hero.personalEmailWarning")}</p>
          <button
            onClick={handleContinueAnyway}
            className={clsx(
              "mt-2 underline underline-offset-2 text-xs transition-colors animate-[fadeSlideIn_0.3s_ease-out_0.1s_both]",
              variant === "dark"
                ? "text-white/70 hover:text-white"
                : "text-black/50 hover:text-black",
            )}
          >
            {t("hero.continueAnyway")}
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailInput;
